import { useEffect, useMemo, useState } from "react";
import { fr } from "date-fns/locale";
import { Link } from "react-router-dom";
import { CheckCircle2, CalendarDays, Clock, Loader2, Home, FileText } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "@/components/Reveal";
import { PaymentChoice } from "@/components/PaymentChoice";
import { GiftCodeField, remiseCadeau } from "@/components/GiftCodeField";
import { ModePaiement, ACOMPTE } from "@/components/ModePaiement";
import { api, formatCHF } from "@/lib/api";

const inputCls = "w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra";
const chipCls = (active, disabled) =>
  `px-3 py-3 rounded-xl border text-xs font-mono transition-colors duration-300 ${
    disabled ? "border-wood/10 text-fumee/40 line-through cursor-not-allowed" : active ? "border-terra bg-terra/10 text-terra" : "border-wood/25 text-fumee hover:border-wood/50"
  }`;

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const longDate = (d) => new Intl.DateTimeFormat("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(d);
const hLabel = (hhmm) => `${parseInt(hhmm, 10)} h`;
const addH = (hhmm, h) => `${String(parseInt(hhmm, 10) + h).padStart(2, "0")}:00`;

const DEFAULT_LIEU = { creneaux: ["09:00", "12:00", "15:00", "18:00"], duree_h: 2, ecart_h: 1, delai_jours: 3,
  tarifs: { pieces_1: { label: "1 à 1,5 pièce", prix: 150 }, pieces_2: { label: "2 à 2,5 pièces", prix: 250 }, pieces_3: { label: "3 à 3,5 pièces", prix: 350 }, pieces_4: { label: "4 à 4,5 pièces", prix: 450 }, pieces_5: { label: "5 à 5,5 pièces", prix: 500 } },
  devis: "Grands lieux, entreprises et demandes hors barème : sur devis" };
const joursFermes = (dispo) => [{ dayOfWeek: dispo.jours_fermes || [0] }, ...(dispo.fermetures || []).map((f) => ({ from: new Date(`${f.du}T00:00:00`), to: new Date(`${f.au}T23:59:59`) }))];

const creneauLibre = (jour, heure, occupations, cfg, dureeH) => {
  const debut = new Date(`${jour}T${heure}:00`);
  const fin = new Date(debut.getTime() + dureeH * 3600e3);
  const ecart = cfg.ecart_h * 3600e3;
  return (occupations[jour] || []).every(([od, of]) => !(debut < new Date(new Date(of).getTime() + ecart) && new Date(fin.getTime() + ecart) > new Date(od)));
};

const SUIVIS = ["suivi", "suivi_annuel"];

export const LieuBooking = ({ prefill = null, suivi = false }) => {
  const [dispo, setDispo] = useState({ occupations: {}, lieu: DEFAULT_LIEU, jours_fermes: [0], fermetures: [], agenda_verifie: false });
  const [selected, setSelected] = useState(undefined);
  const [heure, setHeure] = useState(null);
  const [form, setForm] = useState({ type_lieu: suivi ? "suivi" : "pieces_2", adresse_lieu: prefill?.adresse_lieu || "", nom: prefill?.nom || "", email: prefill?.email || "", telephone: prefill?.telephone || "", paiement: "en_ligne", mode_paiement: "total", message: "" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);
  const [carte, setCarte] = useState(null);

  const refresh = () => api.disponibilites().then((d) => setDispo({ occupations: d.occupations || {}, lieu: d.lieu || DEFAULT_LIEU, jours_fermes: (d.jours_fermes || [6]).map((j) => (j + 1) % 7), fermetures: d.fermetures || [], agenda_verifie: !!d.agenda_verifie })).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const cfg = { ...dispo.lieu, tarifs: Object.fromEntries(Object.entries(dispo.lieu.tarifs).filter(([k]) => SUIVIS.includes(k) === suivi)) };
  const dureeH = suivi ? 1 : cfg.duree_h;
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + cfg.delai_jours);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 180);

  const jour = selected ? toKey(selected) : null;
  const creneaux = useMemo(() => cfg.creneaux.map((h) => ({ h, libre: jour ? creneauLibre(jour, h, dispo.occupations, cfg, dureeH) : true })), [jour, dispo, cfg, dureeH]);
  const disabled = useMemo(() => joursFermes(dispo), [dispo]);

  useEffect(() => { setHeure(null); }, [jour]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));
  const prix = cfg.tarifs[form.type_lieu]?.prix || 0;
  const remise = remiseCadeau(carte, prix);
  const total = prix - remise;

  const submit = async (e) => {
    e.preventDefault();
    if (!selected || !heure) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.book({ type: "lieu", date_individuelle: jour, heure, ...form, suivi_de: prefill?.id || null, participants: 1, origin_url: window.location.origin, code_cadeau: carte?.code || null });
      if (res.checkout_url) { window.location.assign(res.checkout_url); return; }
      setConfirmation(res);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue, merci de réessayer.");
      refresh();
    } finally {
      setLoading(false);
    }
  };

  if (confirmation) {
    return (
      <Reveal>
        <div className="bg-sage-light/50 border border-sage/30 rounded-2xl p-10 text-center" data-testid="lieu-success">
          <CheckCircle2 className="w-12 h-12 text-sage mx-auto mb-4" />
          <p className="font-serif text-3xl text-bark mb-3">{suivi ? "Votre suivi est réservé" : "Votre visite est réservée"}</p>
          <p className="text-fumee max-w-md mx-auto">
            Référence <span className="font-mono text-terra">{confirmation.reference}</span> —{" "}
            <span className="capitalize">{longDate(new Date(confirmation.event_date))}</span>, {confirmation.horaire} ·{" "}
            {formatCHF(confirmation.montant)} · {confirmation.paiement === "carte_cadeau" ? "réglé par carte cadeau" : "règlement par facture sous 10 jours"}.
            Un courriel de confirmation arrive vers {confirmation.email}.
          </p>
          <p className="text-sm text-terra mt-4 font-mono tracking-wide" data-testid="lieu-confirmation-adresse">{confirmation.type_lieu_label} · {confirmation.adresse_lieu}</p>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal>
      <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="lieu-booking">
        <div className="bg-card border border-sand-dark rounded-2xl p-5 sm:p-6 lg:p-8 min-w-0">
          <p className="eyebrow mb-4 flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Choisissez le jour de la visite</p>
          <div data-testid="lieu-calendar" className="flex justify-center overflow-x-auto">
            <Calendar mode="single" selected={selected} onSelect={setSelected} disabled={disabled} fromDate={minDate} toDate={maxDate} locale={fr} weekStartsOn={1} showOutsideDays={false} />
          </div>
          <div className="mt-5">
            <p className="eyebrow mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Créneau de départ</p>
            <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Créneau horaire">
              {creneaux.map(({ h, libre }) => (
                <button type="button" key={h} disabled={!selected || !libre} data-testid={`lieu-creneau-${h.slice(0, 2)}`}
                  onClick={() => setHeure(h)} className={chipCls(heure === h, selected && !libre)}>
                  {hLabel(h)} – {hLabel(addH(h, dureeH))}
                </button>
              ))}
            </div>
            <p className="text-xs text-fumee mt-3 leading-relaxed">
              {suivi ? "Un suivi dure environ 1 h" : `Une visite dure environ ${cfg.duree_h} h`} ; une heure de battement est gardée entre deux rendez-vous. Jours fermés et créneaux déjà pris sont grisés{dispo.agenda_verifie ? ", agenda de Ludovic vérifié en direct" : ""}. Suisse romande — autres régions sur demande.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-card border border-sand-dark rounded-2xl p-5 sm:p-6 lg:p-8 space-y-4 min-w-0" data-testid="lieu-form">
          <div className="flex items-baseline justify-between gap-4">
            <p className="font-serif text-2xl text-bark">Votre lieu & vous</p>
            <span className={`font-mono text-xs tracking-wider text-right ${selected && heure ? "text-terra" : "text-fumee"}`} data-testid="lieu-date-choisie">
              {selected ? `${longDate(selected)}${heure ? ` · ${hLabel(heure)}` : ""}` : "Aucune date choisie"}
            </span>
          </div>
          <div>
            <span className="eyebrow block mb-2">{suivi ? "Type de suivi" : "Taille du lieu"}</span>
            <div className={`grid gap-2 ${suivi ? "sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`} role="radiogroup" aria-label={suivi ? "Type de suivi" : "Taille du lieu"}>
              {Object.entries(cfg.tarifs).map(([k, t]) => (
                <button type="button" key={k} data-testid={`lieu-type-${k}`} onClick={() => setForm((prev) => ({ ...prev, type_lieu: k }))}
                  className={`flex flex-col items-start text-left rounded-xl border px-3 py-3 transition-colors duration-300 ${form.type_lieu === k ? "border-terra bg-terra/5" : "border-wood/20 hover:border-wood/40"}`}>
                  <span className={`text-sm font-medium leading-snug ${form.type_lieu === k ? "text-terra" : "text-bark"}`}>{t.label}</span>
                  <span className="font-mono text-xs text-terra mt-2">{formatCHF(t.prix)}</span>
                </button>
              ))}
              {!suivi && (
                <Link to="/contact?sujet=devis-lieu" data-testid="lieu-type-devis" className="flex flex-col items-start text-left rounded-xl border border-dashed border-wood/30 px-3 py-3 hover:border-terra transition-colors duration-300">
                  <span className="text-sm font-medium text-bark leading-snug flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-terra" /> Plus de 5,5 pièces, entreprise</span>
                  <span className="font-mono text-xs text-terra mt-2">Sur devis →</span>
                </Link>
              )}
            </div>
            {!suivi && <p className="text-[11px] text-fumee mt-2 leading-snug" data-testid="lieu-devis-note">{cfg.devis || DEFAULT_LIEU.devis}. Visite d'environ {cfg.duree_h} h quelle que soit la taille.</p>}
          </div>
          <div>
            <label htmlFor="lieu-adresse" className="eyebrow block mb-2 flex items-center gap-2"><Home className="w-3.5 h-3.5" /> Adresse complète du lieu</label>
            <input id="lieu-adresse" data-testid="lieu-adresse" required minLength={8} readOnly={!!prefill} value={form.adresse_lieu} onChange={set("adresse_lieu")} placeholder="Rue, numéro, NPA et localité" className={inputCls} />
          </div>
          <div className="border-t border-sand-dark pt-4 grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="lieu-nom" className="eyebrow block mb-2">Nom complet</label>
              <input id="lieu-nom" data-testid="lieu-nom" required value={form.nom} onChange={set("nom")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="lieu-email" className="eyebrow block mb-2">Courriel</label>
              <input id="lieu-email" data-testid="lieu-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
            </div>
          </div>
          <div>
            <label htmlFor="lieu-tel" className="eyebrow block mb-2">Téléphone</label>
            <input id="lieu-tel" data-testid="lieu-telephone" type="tel" required minLength={6} value={form.telephone} onChange={set("telephone")} className={inputCls} />
          </div>
          <div>
            <span className="eyebrow block mb-2">Paiement</span>
            <PaymentChoice compact value={form.paiement} onChange={(v) => setForm((prev) => ({ ...prev, paiement: v }))} testidPrefix="lieu" />
          </div>
          <GiftCodeField carte={carte} onChange={setCarte} testidPrefix="lieu" />
          {form.paiement === "en_ligne" && (
            <div>
              <span className="eyebrow block mb-2">Régler</span>
              <ModePaiement value={form.mode_paiement} onChange={(v) => setForm((prev) => ({ ...prev, mode_paiement: v }))} total={total} testidPrefix="lieu" jourLabel="le jour de la visite" />
            </div>
          )}
          <div>
            <label htmlFor="lieu-msg" className="eyebrow block mb-2">Vos ressentis (optionnel)</label>
            <textarea id="lieu-msg" data-testid="lieu-message" rows={3} value={form.message} onChange={set("message")}
              placeholder="Lourdeur dans certaines pièces, sommeil perturbé, emménagement récent, événement marquant…" className={`${inputCls} resize-none`} />
          </div>
          {error && <p data-testid="lieu-error" className="text-sm text-destructive">{error}</p>}
          <div className="flex items-center justify-between pt-2">
            <div>
              <span className="font-serif text-xl text-bark" data-testid="lieu-total">{formatCHF(total)}</span>
              {remise > 0 && <p className="text-[11px] text-terra mt-1" data-testid="lieu-remise">carte cadeau : − {formatCHF(remise)}</p>}
              {form.paiement === "en_ligne" && form.mode_paiement === "acompte" && total > ACOMPTE && (
                <p className="text-[11px] text-terra mt-1" data-testid="lieu-acompte-info">aujourd'hui : {formatCHF(ACOMPTE)} · solde {formatCHF(total - ACOMPTE)}</p>
              )}
            </div>
            <button type="submit" data-testid="lieu-submit" disabled={loading || !selected || !heure}
              className="px-7 py-3 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-earth transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Un instant…" : form.paiement === "en_ligne" && total > 0 ? "Réserver et payer (TWINT / carte)" : suivi ? "Réserver ce suivi" : "Réserver cette visite"}
            </button>
          </div>
        </form>
      </div>
    </Reveal>
  );
};
